import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { scriptTrackerApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { VERTICALS } from '../constants/verticals';
import {
  Plus, Trash2, X, ChevronDown, ChevronRight, Filter,
  ArrowUpDown, Layers, Calendar, List, ExternalLink, Check
} from 'lucide-react';

// ─── Types ──────────────────────────────────────────────────
interface ScriptEntry {
  id: string;
  script_title: string;
  date: string;
  vertical: string | null;
  notes: string | null;
  status: 'todo' | 'in_progress' | 'done';
  paste_links: string | null;
  editor_name: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

interface CreativeUser {
  id: string;
  name: string;
}

// ─── Constants ──────────────────────────────────────────────
// Generate colors dynamically for all verticals
const VERTICAL_COLOR_PALETTE = [
  { bg: 'bg-green-500', text: 'text-white' },
  { bg: 'bg-blue-500', text: 'text-white' },
  { bg: 'bg-orange-500', text: 'text-white' },
  { bg: 'bg-teal-500', text: 'text-white' },
  { bg: 'bg-emerald-500', text: 'text-white' },
  { bg: 'bg-yellow-600', text: 'text-white' },
  { bg: 'bg-purple-500', text: 'text-white' },
  { bg: 'bg-red-500', text: 'text-white' },
  { bg: 'bg-indigo-500', text: 'text-white' },
  { bg: 'bg-pink-500', text: 'text-white' },
  { bg: 'bg-cyan-600', text: 'text-white' },
  { bg: 'bg-lime-600', text: 'text-white' },
  { bg: 'bg-amber-600', text: 'text-white' },
  { bg: 'bg-violet-500', text: 'text-white' },
  { bg: 'bg-rose-500', text: 'text-white' },
  { bg: 'bg-sky-500', text: 'text-white' },
  { bg: 'bg-fuchsia-500', text: 'text-white' },
  { bg: 'bg-stone-500', text: 'text-white' },
  { bg: 'bg-slate-500', text: 'text-white' },
  { bg: 'bg-zinc-500', text: 'text-white' },
  { bg: 'bg-neutral-600', text: 'text-white' },
  { bg: 'bg-green-600', text: 'text-white' },
  { bg: 'bg-blue-600', text: 'text-white' },
  { bg: 'bg-orange-600', text: 'text-white' },
  { bg: 'bg-teal-600', text: 'text-white' },
  { bg: 'bg-purple-600', text: 'text-white' },
  { bg: 'bg-red-600', text: 'text-white' },
  { bg: 'bg-indigo-600', text: 'text-white' },
];

const VERTICAL_COLORS: Record<string, { bg: string; text: string }> = {};
VERTICALS.forEach((v, i) => {
  VERTICAL_COLORS[v] = VERTICAL_COLOR_PALETTE[i % VERTICAL_COLOR_PALETTE.length];
});

const STATUS_CONFIG: Record<string, { bg: string; text: string; label: string }> = {
  'done': { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-400', label: 'Done' },
  'todo': { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-400', label: 'Todo' },
  'in_progress': { bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-700 dark:text-yellow-400', label: 'In progress' },
};

type GroupByField = 'none' | 'date' | 'vertical' | 'status' | 'editor_name';
type ViewMode = 'grid' | 'calendar';

// ─── Helper Functions ───────────────────────────────────────
function formatDate(dateStr: string) {
  const d = new Date(dateStr);
  return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
}

function formatTime(dateStr: string) {
  const d = new Date(dateStr);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function formatDateForInput(dateStr: string) {
  return new Date(dateStr).toISOString().slice(0, 16);
}

function getGroupKey(entry: ScriptEntry, groupBy: GroupByField): string {
  if (groupBy === 'date') return formatDate(entry.date);
  if (groupBy === 'vertical') return entry.vertical || 'No Vertical';
  if (groupBy === 'status') return STATUS_CONFIG[entry.status]?.label || entry.status;
  if (groupBy === 'editor_name') return entry.editor_name || 'None';
  return 'all';
}

function getVerticalColor(vertical: string) {
  return VERTICAL_COLORS[vertical] || { bg: 'bg-gray-500', text: 'text-white' };
}

function parseMulti(value: string | null): string[] {
  if (!value) return [];
  return value.split(',').map(s => s.trim()).filter(Boolean);
}

function joinMulti(arr: string[]): string {
  return arr.filter(Boolean).join(', ');
}

// ─── Portal Dropdown Component ──────────────────────────────
// Renders dropdown in a portal so it's never clipped by overflow:hidden/auto
function PortalDropdown({ anchorRef, children, onClose }: {
  anchorRef: React.RefObject<HTMLElement | null>;
  children: React.ReactNode;
  onClose: () => void;
}) {
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, left: rect.left });
    }
  }, [anchorRef]);

  return ReactDOM.createPortal(
    <>
      <div className="fixed inset-0 z-[9998]" onClick={(e) => { e.stopPropagation(); onClose(); }} />
      <div
        className="fixed z-[9999] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl"
        style={{ top: pos.top, left: pos.left }}
        onClick={e => e.stopPropagation()}
      >
        {children}
      </div>
    </>,
    document.body
  );
}

// ─── Multi-Checkbox Dropdown Content ────────────────────────
function MultiCheckboxContent({
  selected,
  options,
  onChange,
  onClose,
  showCustomInput,
  placeholder,
  renderOption,
}: {
  selected: string[];
  options: string[];
  onChange: (selected: string[]) => void;
  onClose: () => void;
  showCustomInput?: boolean;
  placeholder?: string;
  renderOption?: (option: string) => React.ReactNode;
}) {
  const [customValue, setCustomValue] = useState('');

  const toggle = (opt: string) => {
    const next = selected.includes(opt) ? selected.filter(s => s !== opt) : [...selected, opt];
    onChange(next);
  };

  const addCustom = () => {
    const val = customValue.trim();
    if (val && !selected.includes(val)) {
      onChange([...selected, val]);
    }
    setCustomValue('');
  };

  return (
    <div className="py-1 min-w-[220px] max-h-[320px] overflow-y-auto">
      {options.map(opt => {
        const isChecked = selected.includes(opt);
        return (
          <button
            key={opt}
            onClick={() => toggle(opt)}
            className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
          >
            <input type="checkbox" checked={isChecked} readOnly className="w-3.5 h-3.5 rounded border-gray-300 text-blue-500 pointer-events-none" />
            {renderOption ? renderOption(opt) : <span>{opt}</span>}
          </button>
        );
      })}
      {showCustomInput && (
        <div className="border-t dark:border-gray-700 px-3 py-2 flex gap-1">
          <input
            type="text"
            value={customValue}
            onChange={e => setCustomValue(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustom(); } }}
            placeholder={placeholder || 'Custom...'}
            className="flex-1 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none"
          />
          <button onClick={addCustom} className="text-xs text-blue-500 hover:text-blue-700 font-medium px-1">Add</button>
        </div>
      )}
      <div className="border-t dark:border-gray-700 px-3 py-1.5 flex justify-end">
        <button onClick={onClose} className="text-xs text-blue-500 hover:text-blue-700 font-medium">Done</button>
      </div>
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────
export function ScriptTrackerPage() {
  const { user } = useAuth();
  const [entries, setEntries] = useState<ScriptEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);

  // View state
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [groupBy, setGroupBy] = useState<GroupByField>('none');
  const [showGroupMenu, setShowGroupMenu] = useState(false);
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  // Filter state
  const [filterStatus, setFilterStatus] = useState('');
  const [filterVertical, setFilterVertical] = useState('');

  // Edit state
  const [editingCell, setEditingCell] = useState<{ id: string; field: string } | null>(null);
  const [editValue, setEditValue] = useState('');

  // Notes popup
  const [notesPopup, setNotesPopup] = useState<ScriptEntry | null>(null);

  // Dropdowns — store the entry ID that has an open dropdown
  const [activeDropdown, setActiveDropdown] = useState<{ id: string; type: 'vertical' | 'status' | 'editor' } | null>(null);
  const dropdownAnchorRef = useRef<HTMLButtonElement | null>(null);

  // Add row
  const [showAddRow, setShowAddRow] = useState(false);
  const [newEntry, setNewEntry] = useState({
    script_title: '',
    date: new Date().toISOString().slice(0, 16),
    vertical: '',
    notes: '',
    status: 'todo' as const,
    paste_links: '',
    editor_name: '',
  });

  // New row dropdowns
  const [newRowDropdown, setNewRowDropdown] = useState<'vertical' | 'editor' | null>(null);
  const newRowAnchorRef = useRef<HTMLButtonElement | null>(null);

  // Known verticals and creative users
  const [knownVerticals, setKnownVerticals] = useState<string[]>([...VERTICALS]);
  const [creativeUsers, setCreativeUsers] = useState<CreativeUser[]>([]);

  // Calendar
  const [calendarMonth, setCalendarMonth] = useState(new Date());

  // ─── Fetch ────────────────────────────────────────────────
  const fetchEntries = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = { sort_by: sortBy, sort_order: sortOrder };
      if (filterStatus) params.status = filterStatus;
      if (filterVertical) params.vertical = filterVertical;
      const res = await scriptTrackerApi.getAll(params);
      setEntries(res.data.data || []);
    } catch (err) {
      console.error('Failed to fetch script tracker entries:', err);
    } finally {
      setLoading(false);
    }
  }, [sortBy, sortOrder, filterStatus, filterVertical]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  useEffect(() => {
    scriptTrackerApi.getVerticals().then(res => {
      const verts: string[] = res.data.data || [];
      const merged = Array.from(new Set([...VERTICALS, ...verts]));
      setKnownVerticals(merged);
    }).catch(() => {});
    scriptTrackerApi.getEditors().then(res => {
      setCreativeUsers(res.data.data || []);
    }).catch(() => {});
  }, []);

  // ─── CRUD ─────────────────────────────────────────────────
  const handleCreate = async () => {
    if (!newEntry.script_title.trim()) return;
    try {
      await scriptTrackerApi.create(newEntry);
      setNewEntry({
        script_title: '',
        date: new Date().toISOString().slice(0, 16),
        vertical: '',
        notes: '',
        status: 'todo',
        paste_links: '',
        editor_name: '',
      });
      setShowAddRow(false);
      fetchEntries();
    } catch (err) {
      console.error('Failed to create entry:', err);
    }
  };

  const handleUpdate = async (id: string, field: string, value: any) => {
    setSaving(id);
    try {
      await scriptTrackerApi.update(id, { [field]: value });
      setEntries(prev => prev.map(e => e.id === id ? { ...e, [field]: value } : e));
    } catch (err) {
      console.error('Failed to update entry:', err);
    } finally {
      setSaving(null);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      await scriptTrackerApi.delete(id);
      setEntries(prev => prev.filter(e => e.id !== id));
    } catch (err) {
      console.error('Failed to delete entry:', err);
    }
  };

  const startEdit = (id: string, field: string, currentValue: string) => {
    setEditingCell({ id, field });
    setEditValue(currentValue || '');
  };

  const commitEdit = () => {
    if (editingCell) {
      handleUpdate(editingCell.id, editingCell.field, editValue);
      setEditingCell(null);
      setEditValue('');
    }
  };

  const cancelEdit = () => {
    setEditingCell(null);
    setEditValue('');
  };

  // ─── Grouping ─────────────────────────────────────────────
  const groupedEntries = React.useMemo(() => {
    if (groupBy === 'none') return { 'all': entries };
    const groups: Record<string, ScriptEntry[]> = {};
    entries.forEach(entry => {
      const key = getGroupKey(entry, groupBy);
      if (!groups[key]) groups[key] = [];
      groups[key].push(entry);
    });
    return groups;
  }, [entries, groupBy]);

  const toggleGroup = (key: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // ─── Calendar ─────────────────────────────────────────────
  const calendarDays = React.useMemo(() => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDow = firstDay.getDay();
    const days: (Date | null)[] = [];
    for (let i = 0; i < startDow; i++) days.push(null);
    for (let d = 1; d <= lastDay.getDate(); d++) days.push(new Date(year, month, d));
    return days;
  }, [calendarMonth]);

  const entriesByDate = React.useMemo(() => {
    const map: Record<string, ScriptEntry[]> = {};
    entries.forEach(e => {
      const d = new Date(e.date);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (!map[key]) map[key] = [];
      map[key].push(e);
    });
    return map;
  }, [entries]);

  const creativeNamesList = React.useMemo(() => creativeUsers.map(u => u.name), [creativeUsers]);

  // ─── Open dropdown helper ─────────────────────────────────
  const openDropdown = (entryId: string, type: 'vertical' | 'status' | 'editor', btnRef: HTMLButtonElement) => {
    dropdownAnchorRef.current = btnRef;
    setActiveDropdown(prev =>
      prev?.id === entryId && prev?.type === type ? null : { id: entryId, type }
    );
  };

  // ─── Render Helpers ───────────────────────────────────────
  const renderVerticalTag = (vertical: string | null, entryId: string) => {
    const selected = parseMulti(vertical);
    const isOpen = activeDropdown?.id === entryId && activeDropdown?.type === 'vertical';
    return (
      <div className="relative">
        <button
          ref={el => { if (isOpen && el) dropdownAnchorRef.current = el; }}
          onClick={(e) => { e.stopPropagation(); openDropdown(entryId, 'vertical', e.currentTarget); }}
          className="flex items-center gap-1 flex-wrap"
        >
          {selected.length > 0 ? (
            selected.map(v => {
              const c = getVerticalColor(v);
              return <span key={v} className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium ${c.bg} ${c.text} mr-0.5 mb-0.5`}>{v}</span>;
            })
          ) : (
            <span className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 px-2 py-1">+ Add</span>
          )}
        </button>
        {isOpen && (
          <PortalDropdown anchorRef={dropdownAnchorRef} onClose={() => setActiveDropdown(null)}>
            <MultiCheckboxContent
              selected={selected}
              options={knownVerticals}
              onChange={(sel) => handleUpdate(entryId, 'vertical', joinMulti(sel))}
              onClose={() => setActiveDropdown(null)}
              renderOption={(opt) => {
                const c = getVerticalColor(opt);
                return <><span className={`w-3 h-3 rounded ${c.bg} inline-block`} /><span>{opt}</span></>;
              }}
            />
          </PortalDropdown>
        )}
      </div>
    );
  };

  const renderEditorName = (editorName: string | null, entryId: string) => {
    const selected = parseMulti(editorName);
    const isOpen = activeDropdown?.id === entryId && activeDropdown?.type === 'editor';
    return (
      <div className="relative">
        <button
          ref={el => { if (isOpen && el) dropdownAnchorRef.current = el; }}
          onClick={(e) => { e.stopPropagation(); openDropdown(entryId, 'editor', e.currentTarget); }}
          className="text-xs text-left"
        >
          {selected.length > 0 ? (
            <span className="text-gray-800 dark:text-gray-200">{selected.join(', ')}</span>
          ) : (
            <span className="text-gray-400 italic">None</span>
          )}
        </button>
        {isOpen && (
          <PortalDropdown anchorRef={dropdownAnchorRef} onClose={() => setActiveDropdown(null)}>
            <MultiCheckboxContent
              selected={selected}
              options={creativeNamesList}
              onChange={(sel) => handleUpdate(entryId, 'editor_name', joinMulti(sel))}
              onClose={() => setActiveDropdown(null)}
              showCustomInput
              placeholder="Enter custom name..."
            />
          </PortalDropdown>
        )}
      </div>
    );
  };

  const renderStatusBadge = (status: string, entryId: string) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG['todo'];
    const isOpen = activeDropdown?.id === entryId && activeDropdown?.type === 'status';
    return (
      <div className="relative">
        <button
          ref={el => { if (isOpen && el) dropdownAnchorRef.current = el; }}
          onClick={(e) => { e.stopPropagation(); openDropdown(entryId, 'status', e.currentTarget); }}
          className={`inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium ${config.bg} ${config.text}`}
        >
          {config.label}
        </button>
        {isOpen && (
          <PortalDropdown anchorRef={dropdownAnchorRef} onClose={() => setActiveDropdown(null)}>
            <div className="py-1 min-w-[120px]">
              {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                <button
                  key={key}
                  onClick={() => { handleUpdate(entryId, 'status', key); setActiveDropdown(null); }}
                  className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                >
                  <span className={`px-2 py-0.5 rounded ${cfg.bg} ${cfg.text}`}>{cfg.label}</span>
                </button>
              ))}
            </div>
          </PortalDropdown>
        )}
      </div>
    );
  };

  const renderCell = (entry: ScriptEntry, field: string) => {
    const isEditing = editingCell?.id === entry.id && editingCell?.field === field;

    if (field === 'vertical') return renderVerticalTag(entry.vertical, entry.id);
    if (field === 'status') return renderStatusBadge(entry.status, entry.id);
    if (field === 'editor_name') return renderEditorName(entry.editor_name, entry.id);

    if (field === 'notes') {
      return (
        <button
          onClick={() => setNotesPopup(entry)}
          className="text-left text-xs text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 truncate max-w-[200px] block"
          title={entry.notes || ''}
        >
          {entry.notes ? (
            entry.notes.length > 40 ? entry.notes.slice(0, 40).replace(/\n/g, ' ') + '...' : entry.notes.replace(/\n/g, ' ')
          ) : (
            <span className="text-gray-400 italic">Click to add</span>
          )}
        </button>
      );
    }

    if (field === 'paste_links') {
      if (isEditing) {
        return (
          <input
            autoFocus
            type="text"
            value={editValue}
            onChange={e => setEditValue(e.target.value)}
            onBlur={commitEdit}
            onKeyDown={e => { if (e.key === 'Enter') commitEdit(); if (e.key === 'Escape') cancelEdit(); }}
            className="w-full px-1 py-0.5 text-xs border border-blue-400 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none"
            placeholder="Paste link..."
          />
        );
      }
      if (entry.paste_links) {
        const url = entry.paste_links.startsWith('http') ? entry.paste_links : `https://${entry.paste_links}`;
        // Show truncated URL as clickable link
        const displayUrl = entry.paste_links.replace(/^https?:\/\//, '').replace(/\/$/, '');
        return (
          <div className="flex items-center gap-1 text-xs">
            <a href={url} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline truncate max-w-[140px] flex items-center gap-1" onClick={e => e.stopPropagation()}>
              <ExternalLink className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">{displayUrl.length > 25 ? displayUrl.slice(0, 25) + '...' : displayUrl}</span>
            </a>
            <button onClick={() => startEdit(entry.id, field, entry.paste_links || '')} className="text-gray-400 hover:text-gray-600 flex-shrink-0" title="Edit link">
              <span className="text-[10px]">&#9998;</span>
            </button>
          </div>
        );
      }
      return (
        <span
          className="text-xs text-gray-400 italic cursor-pointer hover:text-gray-600"
          onClick={() => startEdit(entry.id, field, '')}
        >
          Add link
        </span>
      );
    }

    if (field === 'date') {
      if (isEditing) {
        return (
          <input
            autoFocus
            type="datetime-local"
            value={editValue}
            onChange={e => setEditValue(e.target.value)}
            onBlur={commitEdit}
            onKeyDown={e => { if (e.key === 'Enter') commitEdit(); if (e.key === 'Escape') cancelEdit(); }}
            className="w-full px-1 py-0.5 text-xs border border-blue-400 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none"
          />
        );
      }
      return (
        <div className="flex gap-2 text-xs cursor-pointer" onClick={() => startEdit(entry.id, 'date', formatDateForInput(entry.date))}>
          <span>{formatDate(entry.date)}</span>
          <span className="text-gray-500">{formatTime(entry.date)}</span>
        </div>
      );
    }

    // Default text fields: script_title
    if (isEditing) {
      return (
        <input
          autoFocus
          type="text"
          value={editValue}
          onChange={e => setEditValue(e.target.value)}
          onBlur={commitEdit}
          onKeyDown={e => { if (e.key === 'Enter') commitEdit(); if (e.key === 'Escape') cancelEdit(); }}
          className="w-full px-1 py-0.5 text-xs border border-blue-400 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none"
        />
      );
    }

    const value = (entry as any)[field] || '';
    return (
      <span
        className="text-xs cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 px-1 py-0.5 rounded block truncate"
        onClick={() => startEdit(entry.id, field, value)}
      >
        {value || <span className="text-gray-400 italic">Click to edit</span>}
      </span>
    );
  };

  // ─── Row Rendering ────────────────────────────────────────
  const renderRow = (entry: ScriptEntry, index: number) => (
    <tr key={entry.id} className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
      <td className="px-3 py-2 text-xs text-gray-500 w-10 text-center">{index + 1}</td>
      <td className="px-3 py-2 min-w-[180px]">{renderCell(entry, 'script_title')}</td>
      <td className="px-3 py-2 min-w-[140px]">{renderCell(entry, 'date')}</td>
      <td className="px-3 py-2 min-w-[140px]">{renderCell(entry, 'vertical')}</td>
      <td className="px-3 py-2 min-w-[200px]">{renderCell(entry, 'notes')}</td>
      <td className="px-3 py-2 min-w-[100px]">{renderCell(entry, 'status')}</td>
      <td className="px-3 py-2 min-w-[140px]">{renderCell(entry, 'paste_links')}</td>
      <td className="px-3 py-2 min-w-[160px]">{renderCell(entry, 'editor_name')}</td>
      <td className="px-3 py-2 w-10">
        <button onClick={() => handleDelete(entry.id)} className="text-gray-400 hover:text-red-500 transition-colors">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </td>
    </tr>
  );

  // ─── Add Row Form ─────────────────────────────────────────
  const renderAddRow = () => {
    const newVerticals = parseMulti(newEntry.vertical);
    const newEditors = parseMulti(newEntry.editor_name);

    return (
      <tr className="border-b border-blue-200 dark:border-blue-700 bg-blue-50/50 dark:bg-blue-900/10">
        <td className="px-3 py-2 text-xs text-gray-500 text-center">
          <Plus className="w-3.5 h-3.5 mx-auto text-blue-500" />
        </td>
        <td className="px-3 py-2">
          <input
            autoFocus
            type="text"
            value={newEntry.script_title}
            onChange={e => setNewEntry({ ...newEntry, script_title: e.target.value })}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) handleCreate(); if (e.key === 'Escape') setShowAddRow(false); }}
            placeholder="Script title..."
            className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </td>
        <td className="px-3 py-2">
          <input
            type="datetime-local"
            value={newEntry.date}
            onChange={e => setNewEntry({ ...newEntry, date: e.target.value })}
            className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </td>
        <td className="px-3 py-2">
          <div className="relative">
            <button
              ref={el => { if (newRowDropdown === 'vertical' && el) newRowAnchorRef.current = el; }}
              onClick={(e) => {
                e.stopPropagation();
                newRowAnchorRef.current = e.currentTarget;
                setNewRowDropdown(newRowDropdown === 'vertical' ? null : 'vertical');
              }}
              className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-left text-gray-900 dark:text-white"
            >
              {newVerticals.length > 0 ? newVerticals.join(', ') : <span className="text-gray-400">Select...</span>}
            </button>
            {newRowDropdown === 'vertical' && (
              <PortalDropdown anchorRef={newRowAnchorRef} onClose={() => setNewRowDropdown(null)}>
                <MultiCheckboxContent
                  selected={newVerticals}
                  options={knownVerticals}
                  onChange={(sel) => setNewEntry({ ...newEntry, vertical: joinMulti(sel) })}
                  onClose={() => setNewRowDropdown(null)}
                  renderOption={(opt) => {
                    const c = getVerticalColor(opt);
                    return <><span className={`w-3 h-3 rounded ${c.bg} inline-block`} /><span>{opt}</span></>;
                  }}
                />
              </PortalDropdown>
            )}
          </div>
        </td>
        <td className="px-3 py-2">
          <textarea
            value={newEntry.notes}
            onChange={e => setNewEntry({ ...newEntry, notes: e.target.value })}
            placeholder="Notes..."
            rows={2}
            className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
          />
        </td>
        <td className="px-3 py-2">
          <select
            value={newEntry.status}
            onChange={e => setNewEntry({ ...newEntry, status: e.target.value as any })}
            className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="todo">Todo</option>
            <option value="in_progress">In progress</option>
            <option value="done">Done</option>
          </select>
        </td>
        <td className="px-3 py-2">
          <input
            type="text"
            value={newEntry.paste_links}
            onChange={e => setNewEntry({ ...newEntry, paste_links: e.target.value })}
            placeholder="Link..."
            className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </td>
        <td className="px-3 py-2">
          <div className="relative">
            <button
              ref={el => { if (newRowDropdown === 'editor' && el) newRowAnchorRef.current = el; }}
              onClick={(e) => {
                e.stopPropagation();
                newRowAnchorRef.current = e.currentTarget;
                setNewRowDropdown(newRowDropdown === 'editor' ? null : 'editor');
              }}
              className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-left text-gray-900 dark:text-white"
            >
              {newEditors.length > 0 ? newEditors.join(', ') : <span className="text-gray-400">Select...</span>}
            </button>
            {newRowDropdown === 'editor' && (
              <PortalDropdown anchorRef={newRowAnchorRef} onClose={() => setNewRowDropdown(null)}>
                <MultiCheckboxContent
                  selected={newEditors}
                  options={creativeNamesList}
                  onChange={(sel) => setNewEntry({ ...newEntry, editor_name: joinMulti(sel) })}
                  onClose={() => setNewRowDropdown(null)}
                  showCustomInput
                  placeholder="Enter custom name..."
                />
              </PortalDropdown>
            )}
          </div>
        </td>
        <td className="px-3 py-2">
          <div className="flex gap-1">
            <button onClick={handleCreate} className="text-green-500 hover:text-green-700"><Check className="w-4 h-4" /></button>
            <button onClick={() => setShowAddRow(false)} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
          </div>
        </td>
      </tr>
    );
  };

  // ─── Grid View ────────────────────────────────────────────
  const renderGridView = () => {
    const columns = [
      { key: '#', label: '', width: 'w-10' },
      { key: 'script_title', label: 'Script Title' },
      { key: 'date', label: 'Date' },
      { key: 'vertical', label: 'Vertical' },
      { key: 'notes', label: 'Notes' },
      { key: 'status', label: 'Status' },
      { key: 'paste_links', label: 'Paste Links' },
      { key: 'editor_name', label: 'Editor Name' },
      { key: 'actions', label: '', width: 'w-10' },
    ];

    let globalIndex = 0;

    return (
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b-2 border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/80">
              {columns.map(col => (
                <th
                  key={col.key}
                  className={`px-3 py-2.5 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 ${col.width || ''}`}
                >
                  {col.label && !['#', 'actions'].includes(col.key) ? (
                    <button
                      className="flex items-center gap-1 hover:text-gray-900 dark:hover:text-gray-200"
                      onClick={() => {
                        if (sortBy === col.key) setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                        else { setSortBy(col.key); setSortOrder('asc'); }
                      }}
                    >
                      {col.label}
                      {sortBy === col.key && <ArrowUpDown className="w-3 h-3" />}
                    </button>
                  ) : col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groupBy === 'none' ? (
              <>
                {entries.map((entry, idx) => renderRow(entry, idx))}
                {showAddRow && renderAddRow()}
                <tr>
                  <td colSpan={9} className="px-3 py-2">
                    <button
                      onClick={() => setShowAddRow(true)}
                      className="flex items-center gap-1 text-xs text-gray-400 hover:text-blue-500 transition-colors"
                    >
                      <Plus className="w-3.5 h-3.5" /> Add row
                    </button>
                  </td>
                </tr>
              </>
            ) : (
              Object.entries(groupedEntries).map(([groupKey, groupItems]) => {
                const isCollapsed = collapsedGroups.has(groupKey);
                const startIdx = globalIndex;
                globalIndex += groupItems.length;

                return (
                  <React.Fragment key={groupKey}>
                    <tr className="bg-gray-100 dark:bg-gray-800">
                      <td colSpan={9} className="px-3 py-2">
                        <button
                          onClick={() => toggleGroup(groupKey)}
                          className="flex items-center gap-2 w-full text-left"
                        >
                          {isCollapsed ? <ChevronRight className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                          <span className="text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">
                            {groupBy === 'date' ? 'DATE' : groupBy.replace('_', ' ').toUpperCase()}
                          </span>
                          <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">{groupKey}</span>
                          <span className="text-xs text-gray-500 ml-1">{groupItems.length}</span>
                        </button>
                      </td>
                    </tr>
                    {!isCollapsed && groupItems.map((entry, idx) => renderRow(entry, startIdx + idx))}
                    {!isCollapsed && (
                      <tr>
                        <td colSpan={9} className="px-3 py-1">
                          <button
                            onClick={() => setShowAddRow(true)}
                            className="flex items-center gap-1 text-xs text-gray-400 hover:text-blue-500 ml-6"
                          >
                            <Plus className="w-3 h-3" />
                          </button>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    );
  };

  // ─── Calendar View ────────────────────────────────────────
  const renderCalendarView = () => {
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    return (
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1))}
            className="px-3 py-1 text-sm border rounded hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            &lt; Prev
          </button>
          <h3 className="text-lg font-semibold">
            {monthNames[calendarMonth.getMonth()]} {calendarMonth.getFullYear()}
          </h3>
          <button
            onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1))}
            className="px-3 py-1 text-sm border rounded hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            Next &gt;
          </button>
        </div>
        <div className="grid grid-cols-7 gap-px bg-gray-200 dark:bg-gray-700 rounded-lg overflow-hidden">
          {dayNames.map(d => (
            <div key={d} className="bg-gray-100 dark:bg-gray-800 px-2 py-2 text-center text-xs font-semibold text-gray-600 dark:text-gray-400">
              {d}
            </div>
          ))}
          {calendarDays.map((day, idx) => {
            if (!day) return <div key={`empty-${idx}`} className="bg-white dark:bg-gray-900 min-h-[100px]" />;
            const key = `${day.getFullYear()}-${day.getMonth()}-${day.getDate()}`;
            const dayEntries = entriesByDate[key] || [];
            const isToday = new Date().toDateString() === day.toDateString();

            return (
              <div key={key} className={`bg-white dark:bg-gray-900 min-h-[100px] p-1 ${isToday ? 'ring-2 ring-blue-500 ring-inset' : ''}`}>
                <div className={`text-xs font-medium mb-1 ${isToday ? 'text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400'}`}>
                  {day.getDate()}
                </div>
                <div className="space-y-0.5">
                  {dayEntries.slice(0, 3).map(e => {
                    const statusCfg = STATUS_CONFIG[e.status];
                    return (
                      <button
                        key={e.id}
                        onClick={() => setNotesPopup(e)}
                        className={`w-full text-left px-1 py-0.5 rounded text-[10px] truncate ${statusCfg.bg} ${statusCfg.text}`}
                        title={e.script_title}
                      >
                        {e.script_title}
                      </button>
                    );
                  })}
                  {dayEntries.length > 3 && (
                    <span className="text-[10px] text-gray-500">+{dayEntries.length - 3} more</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ─── Render ───────────────────────────────────────────────
  return (
    <DashboardLayout title="Script Production Tracker">
      <div className="h-full flex flex-col bg-white dark:bg-gray-900">
        {/* Header */}
        <div className="px-6 py-4 border-b dark:border-gray-700">
          {/* Toolbar */}
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            {/* View toggle */}
            <div className="flex border rounded-lg overflow-hidden">
              <button
                onClick={() => setViewMode('grid')}
                className={`flex items-center gap-1 px-3 py-1.5 text-xs ${viewMode === 'grid' ? 'bg-blue-500 text-white' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
              >
                <List className="w-3.5 h-3.5" /> Grid view
              </button>
              <button
                onClick={() => setViewMode('calendar')}
                className={`flex items-center gap-1 px-3 py-1.5 text-xs ${viewMode === 'calendar' ? 'bg-blue-500 text-white' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
              >
                <Calendar className="w-3.5 h-3.5" /> Calendar
              </button>
            </div>

            {/* Filter */}
            <div className="relative" onClick={e => e.stopPropagation()}>
              <button
                onClick={() => setShowFilterMenu(!showFilterMenu)}
                className={`flex items-center gap-1 px-3 py-1.5 text-xs border rounded-lg transition-colors ${(filterStatus || filterVertical) ? 'bg-blue-50 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-600 dark:text-blue-400' : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400'}`}
              >
                <Filter className="w-3.5 h-3.5" /> Filter
                {(filterStatus || filterVertical) && <span className="w-1.5 h-1.5 bg-blue-500 rounded-full" />}
              </button>
              {showFilterMenu && (
                <div className="absolute z-50 top-full left-0 mt-1 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg shadow-lg p-3 min-w-[200px] space-y-3">
                  <div>
                    <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">Status</label>
                    <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="w-full text-xs px-2 py-1 border rounded dark:bg-gray-700 dark:border-gray-600">
                      <option value="">All</option>
                      <option value="todo">Todo</option>
                      <option value="in_progress">In progress</option>
                      <option value="done">Done</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">Vertical</label>
                    <select value={filterVertical} onChange={e => setFilterVertical(e.target.value)} className="w-full text-xs px-2 py-1 border rounded dark:bg-gray-700 dark:border-gray-600">
                      <option value="">All</option>
                      {knownVerticals.map(v => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </div>
                  {(filterStatus || filterVertical) && (
                    <button onClick={() => { setFilterStatus(''); setFilterVertical(''); }} className="text-xs text-red-500 hover:underline">
                      Clear filters
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Group By */}
            <div className="relative" onClick={e => e.stopPropagation()}>
              <button
                onClick={() => setShowGroupMenu(!showGroupMenu)}
                className={`flex items-center gap-1 px-3 py-1.5 text-xs border rounded-lg transition-colors ${groupBy !== 'none' ? 'bg-blue-50 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-600 dark:text-blue-400' : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400'}`}
              >
                <Layers className="w-3.5 h-3.5" /> Group
                {groupBy !== 'none' && <span className="w-1.5 h-1.5 bg-blue-500 rounded-full" />}
              </button>
              {showGroupMenu && (
                <div className="absolute z-50 top-full left-0 mt-1 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg shadow-lg py-1 min-w-[160px]">
                  <div className="px-3 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-400">Group by</div>
                  {[
                    { key: 'none' as GroupByField, label: 'None' },
                    { key: 'date' as GroupByField, label: 'Date' },
                    { key: 'vertical' as GroupByField, label: 'Vertical' },
                    { key: 'status' as GroupByField, label: 'Status' },
                    { key: 'editor_name' as GroupByField, label: 'Editor Name' },
                  ].map(opt => (
                    <button
                      key={opt.key}
                      onClick={() => { setGroupBy(opt.key); setShowGroupMenu(false); setCollapsedGroups(new Set()); }}
                      className={`w-full text-left px-3 py-1.5 text-xs hover:bg-gray-100 dark:hover:bg-gray-700 ${groupBy === opt.key ? 'text-blue-600 font-medium' : ''}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Sort */}
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="flex items-center gap-1 px-3 py-1.5 text-xs border rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400"
            >
              <ArrowUpDown className="w-3.5 h-3.5" /> Sort {sortOrder === 'asc' ? 'Asc' : 'Desc'}
            </button>

            {/* Add button */}
            <button
              onClick={() => setShowAddRow(true)}
              className="flex items-center gap-1 px-3 py-1.5 text-xs bg-blue-500 text-white rounded-lg hover:bg-blue-600 ml-auto"
            >
              <Plus className="w-3.5 h-3.5" /> Add Record
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <p className="text-gray-500 dark:text-gray-400">Loading...</p>
            </div>
          ) : entries.length === 0 && !showAddRow ? (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <List className="w-12 h-12 text-gray-300 dark:text-gray-600 mb-3" />
              <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-1">No entries yet</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Start tracking your script production</p>
              <button
                onClick={() => setShowAddRow(true)}
                className="flex items-center gap-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm"
              >
                <Plus className="w-4 h-4" /> Add First Record
              </button>
            </div>
          ) : viewMode === 'grid' ? renderGridView() : renderCalendarView()}
        </div>

        {/* Notes Popup */}
        {notesPopup && <NotesPopup entry={notesPopup} onClose={() => setNotesPopup(null)} onSave={handleUpdate} />}
      </div>
    </DashboardLayout>
  );
}

// ─── Notes Popup Component ──────────────────────────────────
function NotesPopup({ entry, onClose, onSave }: { entry: ScriptEntry; onClose: () => void; onSave: (id: string, field: string, value: any) => Promise<void> }) {
  const [editingNotes, setEditingNotes] = useState(entry.notes || '');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setEditingNotes(entry.notes || '');
  }, [entry]);

  const saveNotes = async () => {
    await onSave(entry.id, 'notes', editingNotes);
    setSaved(true);
    setTimeout(() => setSaved(false), 1500);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000] p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white">Notes</h3>
            <p className="text-xs text-gray-500 mt-0.5">{entry.script_title} &middot; {formatDate(entry.date)}</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 p-4 overflow-y-auto">
          <textarea
            value={editingNotes}
            onChange={e => setEditingNotes(e.target.value)}
            className="w-full h-80 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none leading-relaxed"
            placeholder="Add notes... (press Enter for new lines, formatting is preserved)"
          />
        </div>
        <div className="flex items-center justify-between p-4 border-t dark:border-gray-700">
          <p className="text-[10px] text-gray-400">Line breaks and formatting are preserved</p>
          <div className="flex items-center gap-2">
            {saved && <span className="text-xs text-green-500 flex items-center gap-1"><Check className="w-3 h-3" /> Saved</span>}
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              Close
            </button>
            <button
              onClick={saveNotes}
              className="px-3 py-1.5 text-xs bg-blue-500 text-white rounded-lg hover:bg-blue-600"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
